import { FormattedDate } from 'components/misc/FormattedDate';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceMatterDetailsDto, InvoiceType } from 'types/invoices';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

/**
 * Who reviewed and who approved a case, and when, on a joint debit invoice whose cases are reviewed and approved one
 * by one: what the workflow's per-case steps record, whatever step the invoice has reached since.
 */
export const caseSignOffs = (invoice: InvoiceData, invoiceMatter: InvoiceMatterDetailsDto, isCaseByCase: boolean) =>
    isCaseByCase && invoice.invoiceTypeCode === InvoiceType.DEBIT && (invoice.invoiceMatters?.length ?? 0) > 1
        ? [
              {
                  labelId: 'invoices.case-card.reviewed-by',
                  employee: invoiceMatter.reviewedByEmployee,
                  at: invoiceMatter.reviewedAt,
              },
              {
                  labelId: 'invoices.case-card.approved-by',
                  employee: invoiceMatter.approvedByEmployee,
                  at: invoiceMatter.approvedAt,
              },
          ].filter((signOff) => !!signOff.at)
        : [];

/** A case card's line of sign-offs, the people as record chips; nothing until the case has one. */
export function CaseSignOffs({
    invoice,
    invoiceMatter,
}: {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
}) {
    const isCaseByCase = useIsFeatureEnabled(FeatureFlag.MULTI_REVIEW_APPROVE_JOINT_INVOICE);
    const signOffs = caseSignOffs(invoice, invoiceMatter, isCaseByCase);
    if (!signOffs.length) {
        return null;
    }
    return (
        <div className="text-meta text-foreground-tertiary flex w-full flex-wrap items-center gap-x-5 gap-y-1.5">
            {signOffs.map(({ labelId, employee, at }) => (
                <span key={labelId} className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <FormattedMessage
                        id={labelId}
                        values={{
                            name: employee ? (
                                <EmployeeNameLink key="name" entity={employee} to={`/employee/${employee.id}`} />
                            ) : (
                                '-'
                            ),
                            date: <FormattedDate key="date" value={at} />,
                        }}
                    />
                </span>
            ))}
        </div>
    );
}
